
import {
  TableRow,
  TableCell,
  IconButton,
  TextField,
  Typography,
  Table,
  TableHead,
  TableBody,
  Tooltip,
  Paper,
  TableContainer,
  SelectChangeEvent,
  Checkbox,
  useTheme,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { LoginUserInfo } from "IF/interfaceType";

interface SettingUserTableProps {
  rentalFlg: boolean;
  role: string;
  data: LoginUserInfo[];
  editFormData: LoginUserInfo;
  onEdit: (index: number) => void;
  handleChange: (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>,
    child?: React.ReactNode
  ) => void;
  handleCheckboxChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleUpdate: () => void;
  handleCancel: () => void;
  handleDelete: (index: number) => void;
  openModal?: (
    index: number,
    message: string,
    confirmAction: () => void
  ) => void;
  tableBodyHeight: number;
}

const SettingUserTable: React.FC<SettingUserTableProps> = ({
  rentalFlg,
  data,
  editFormData,
  onEdit,
  handleChange,
  handleCheckboxChange,
  handleUpdate,
  handleCancel,
  handleDelete,
  openModal,
  tableBodyHeight,
}) => {
  const theme = useTheme();

  const handleOpenModal = (index: number) => {
    // openModal が存在する場合のみ呼び出す
    openModal?.(
      index,
      "選択したユーザー情報を削除します。よろしいですか？",
      () => handleDelete?.(index)
    );
  };

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          minHeight: tableBodyHeight,
          maxHeight: tableBodyHeight,
          overflowY: "auto",
          backgroundColor: theme.palette.custom.row,
        }}
      >
        <Table>
          <TableHead
            sx={{
              position: "sticky",
              top: 0,
              backgroundColor: theme.palette.custom.tableHeader,
              zIndex: 1,
            }}
          >
            <TableRow>
              {!rentalFlg && (
                <TableCell
                  size={"small"}
                  sx={{
                    width: "10%",
                    color: "white",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  align="center"
                >
                  <Typography fontWeight="fontWeightBold">管理者</Typography>
                </TableCell>
              )}
              <TableCell
                size={"small"}
                sx={{ width: "25%", color: "white" }}
                align="center"
              >
                <Typography fontWeight="fontWeightBold">ログインID</Typography>
              </TableCell>
              <TableCell
                size={"small"}
                sx={{
                  width: "25%",
                  color: "white",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                align="center"
              >
                <Typography fontWeight="fontWeightBold">パスワード</Typography>
              </TableCell>
              <TableCell
                size={"small"}
                sx={{ width: "30%", color: "white" }}
                align="center"
              >
                <Typography fontWeight="fontWeightBold">ユーザー名</Typography>
              </TableCell>
              <TableCell
                size={"small"}
                sx={{
                  width: "10%",
                  color: "white",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                align="center"
              >
                <Typography fontWeight="fontWeightBold">アクション</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((data, index) => (
              <TableRow key={data.id}>
                {!rentalFlg && (
                  <TableCell
                    sx={{
                      width: "10%",
                      textAlign: "center",
                    }}
                    size={"small"}
                  >
                    {editFormData.id === data.id ? (
                      <Checkbox
                        checked={editFormData.adminFlg}
                        onChange={handleCheckboxChange}
                      />
                    ) : (
                      <Checkbox checked={data.adminFlg} disabled />
                    )}
                  </TableCell>
                )}
                <TableCell
                  sx={{ width: "25%", textAlign: "center" }}
                  size={"small"}
                >
                  {editFormData.id === data.id ? (
                    <TextField
                      size={"small"}
                      value={editFormData?.userId}
                      fullWidth
                      name="userId"
                      inputProps={{ maxLength: 16, pattern: "[A-Za-z0-9]*" }}
                      onChange={handleChange}
                    />
                  ) : (
                    data.userId
                  )}
                </TableCell>
                <TableCell
                  size={"small"}
                  sx={{ width: "25%", textAlign: "center" }}
                >
                  {editFormData.id === data.id ? (
                    <TextField
                      size={"small"}
                      value={editFormData?.userPassword}
                      name="userPassword"
                      inputProps={{ maxLength: 16, pattern: "[A-Za-z0-9]*" }}
                      fullWidth
                      onChange={handleChange}
                    />
                  ) : (
                    data.userPassword
                  )}
                </TableCell>
                <TableCell
                  size={"small"}
                  sx={{ width: "30%", textAlign: "center" }}
                >
                  {editFormData.id === data.id ? (
                    <TextField
                      size={"small"}
                      value={editFormData?.userName}
                      name="userName"
                      fullWidth
                      onChange={handleChange}
                    />
                  ) : (
                    data.userName
                  )}
                </TableCell>
                <TableCell
                  size={"small"}
                  sx={{ width: "10%", textAlign: "center" }}
                >
                  {editFormData.id === data.id ? (
                    <>
                      <Tooltip title={"保存"} placement="top" arrow>
                        <IconButton onClick={handleUpdate}>
                          <SaveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={"キャンセル"} placement="top" arrow>
                        <IconButton onClick={handleCancel}>
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip title={"編集"} placement="top" arrow>
                        <IconButton onClick={() => onEdit(index)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {!rentalFlg && (
                        <Tooltip title={"削除"} placement="top" arrow>
                          <IconButton onClick={() => handleOpenModal(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default SettingUserTable;
